(function () {
  const SUPPORTED_STYLES = ["format-card", "image-card", "simple-text"];

  function escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  function escapeAttribute(text) {
    return escapeHtml(text).replace(/"/g, "&quot;");
  }

  function normalizeStyle(style) {
    const normalized = String(style || "")
      .replace(/[-_\s]+(.)?/g, (_, chr = "") => chr.toUpperCase())
      .replace(/^./, (chr) => chr.toLowerCase());

    const styleName =
      {
        imageCard: "image-card",
        formatCard: "format-card",
        simpleText: "simple-text",
      }[normalized] || style;

    return SUPPORTED_STYLES.includes(styleName) ? styleName : "format-card";
  }

  function getVariantViewModel(variant, variants) {
    const variantPrices = variants.map((v) => v.price || 0);
    const maxPrice = Math.max(...variantPrices);
    const minPrice = Math.min(...variantPrices);
    const isPremiumFormat =
      variants.length > 1 &&
      maxPrice > minPrice &&
      (variant.price || 0) === maxPrice;
    const priceDelta = Math.max((variant.price || 0) - minPrice, 0);
    const dimensionMatch = String(variant.title || "").match(
      /(\d+)\s*x\s*(\d+)\s*cm/i,
    );
    const formatTitle = dimensionMatch
      ? `${dimensionMatch[1]} × ${dimensionMatch[2]} cm`
      : variant.title;
    const coverText = dimensionMatch
      ? String(variant.title || "")
          .replace(dimensionMatch[0], "")
          .trim()
      : "";
    const subtitleRaw =
      variant.subtitle != null ? String(variant.subtitle).trim() : "";
    const formatDetail =
      subtitleRaw ||
      (isPremiumFormat
        ? "większe ilustracje, idealny na prezent"
        : "klasyczny format");
    const metaText = [coverText, formatDetail].filter(Boolean).join(" · ");

    return {
      title: variant.title || "",
      displayTitle: formatTitle || "",
      metaText,
      image: variant.form_image || "",
      price: variant.price || 0,
      priceDelta,
      isPremium: isPremiumFormat,
      labelClass: isPremiumFormat ? "pbk-variant--premium" : "",
    };
  }

  function renderImageCardVariantOption(viewModel, context) {
    const imageBlock = viewModel.image
      ? `<img src="${escapeHtml(viewModel.image)}" alt="${escapeHtml(
          viewModel.title,
        )}" class="variant-image" loading="lazy">`
      : "";

    return `
        ${imageBlock}
        <div class="variant-label">
          <div>${escapeHtml(viewModel.title)}</div>
          <div class="variant-price">${context.formatPrice(viewModel.price)}</div>
        </div>
    `;
  }

  function renderFormatCardVariantOption(viewModel, context) {
    const priceDeltaBlock =
      viewModel.priceDelta > 0
        ? `<div class="variant-price-delta">+${context.formatPrice(
            viewModel.priceDelta,
          )}</div>`
        : "";
    const popularBlock = viewModel.isPremium
      ? `<div class="variant-popular">Najpopularniejszy</div>`
      : "";

    return `
        <span class="variant-check" aria-hidden="true"></span>
        <div class="variant-label">
          <div class="variant-title">${escapeHtml(viewModel.displayTitle)}</div>
          <div class="variant-subtitle">${escapeHtml(viewModel.metaText)}</div>
        </div>
        <div class="variant-price-stack">
          ${popularBlock}
          <div class="variant-price">${context.formatPrice(viewModel.price)}</div>
          ${priceDeltaBlock}
          <div class="variant-selected-status">✓ ${escapeHtml(
            context.selectedText,
          )}</div>
        </div>
    `;
  }

  function renderSimpleTextVariantOption(viewModel, context) {
    const priceDeltaBlock =
      viewModel.priceDelta > 0
        ? `<div class="variant-price-delta">+${context.formatPrice(
            viewModel.priceDelta,
          )}</div>`
        : "";

    return `
        <span class="variant-check" aria-hidden="true"></span>
        <div class="variant-label">
          <div class="variant-title">${escapeHtml(viewModel.displayTitle)}</div>
        </div>
        ${priceDeltaBlock}
    `;
  }

  const renderers = {
    "image-card": renderImageCardVariantOption,
    "format-card": renderFormatCardVariantOption,
    "simple-text": renderSimpleTextVariantOption,
  };

  class PersonalizedBookVariantSelector extends HTMLElement {
    connectedCallback() {
      this.variants = this.parseVariants();
      this.instanceId = this.dataset.instanceId || generateId();
      this.styleName = normalizeStyle(this.dataset.style);
      this.render();
    }

    render() {
      const label = this.dataset.label || "Format";

      this.innerHTML = `
        <div class="pbk-variant-radio-row pbk-variant-radio-row--${escapeAttribute(
          this.styleName,
        )}" role="radiogroup" aria-label="${escapeAttribute(
          label,
        )}" data-variant-selector-style="${escapeAttribute(this.styleName)}">
          ${this.variants
            .map((variant, index) => this.renderOption(variant, index))
            .join("")}
        </div>
      `;
    }

    renderOption(variant, index) {
      const radioId = `pbk-variant-${variant.id}-${this.instanceId}`;
      const isChecked = index === 0;
      const viewModel = getVariantViewModel(variant, this.variants);
      const renderer = renderers[this.styleName] || renderers["format-card"];
      const labelClass =
        this.styleName === "format-card" || this.styleName === "simple-text"
          ? viewModel.labelClass
          : "";

      return `
        <input type="radio"
               name="pbk-variant-${this.instanceId}"
               id="${radioId}"
               value="${variant.id}"
               class="pbk-variant-radio"
               ${isChecked ? "checked" : ""}
               required
               data-variant-radio
               data-variant-id="${variant.id}">
        <label for="${radioId}" class="${escapeAttribute(labelClass)}">
          ${renderer(viewModel, {
            formatPrice: (price) => this.formatPrice(price),
            selectedText: this.dataset.selectedText || "Selected",
          })}
        </label>
      `;
    }

    parseVariants() {
      try {
        return JSON.parse(this.dataset.variants || "[]");
      } catch (error) {
        console.error("Error parsing variant selector variants:", error);
        return [];
      }
    }

    formatPrice(priceInCents) {
      const cents =
        typeof priceInCents === "number"
          ? priceInCents
          : parseInt(priceInCents || "0", 10);
      const currencyCodeEnabled = this.parseBoolean(
        this.dataset.currencyCodeEnabled,
        window.theme?.settings?.currencyCodeEnabled,
      );
      const moneyFormat =
        this.dataset.moneyFormat || window.theme?.settings?.moneyFormat || "";
      const moneyWithCurrencyFormat =
        this.dataset.moneyWithCurrencyFormat ||
        window.theme?.settings?.moneyWithCurrencyFormat ||
        "";
      const format =
        currencyCodeEnabled && moneyWithCurrencyFormat
          ? moneyWithCurrencyFormat
          : moneyFormat;

      if (window.theme?.Currency?.formatMoney && format) {
        return window.theme.Currency.formatMoney(cents, format);
      }

      const locale =
        document.documentElement.getAttribute("lang") ||
        navigator.language ||
        "en";
      const currencyIsoCode = this.dataset.currencyIsoCode || "";

      if (currencyIsoCode) {
        try {
          return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: currencyIsoCode,
          }).format(cents / 100);
        } catch (error) {
          // Fall through to the basic formatter below.
        }
      }

      const amount = (cents / 100).toFixed(2);
      const symbol =
        this.dataset.currencySymbol ||
        (window.theme?.settings?.locale === "en" ? "£" : "zł");
      return `${amount} ${symbol}`;
    }

    parseBoolean(value, fallback = false) {
      if (typeof value === "boolean") return value;
      if (value == null || value === "") return Boolean(fallback);
      return String(value).toLowerCase() === "true";
    }
  }

  if (!customElements.get("personalized-book-variant-selector")) {
    customElements.define(
      "personalized-book-variant-selector",
      PersonalizedBookVariantSelector,
    );
  }

  function generateId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
})();
